import React, { useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { StyleSheetManager } from 'styled-components';
import { renderBlocks, type BlockInstance } from '@atlas/blocks';

// Live WYSIWYG preview. Renders the page's blocks inside an isolated iframe so
// the marketing baseline (html font-size 62.5% → 1rem=10px, fonts, media
// queries relative to the frame width) matches the real site exactly, without
// leaking into the MUI admin chrome. styled-components injects into the frame's
// own <head> via StyleSheetManager target.
// `no-js` on <html> triggers the template's own no-JS fallbacks (e.g. owl's
// `.no-js .owl-carousel{display:block}`) so carousels reveal without the hydrator.
const FRAME_HTML = `<!doctype html><html class="no-js"><head><meta charset="utf-8"/>
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@300;400;500;600;700;800&family=Poppins:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap"/>
<style>html{font-size:62.5%}html,body{margin:0;padding:0}body{font-family:'Poppins',sans-serif;color:#0b1020}*,*::before,*::after{box-sizing:border-box}h1,h2,h3,h4,h5,h6,p,ul,figure{margin:0;padding:0}a{color:inherit;text-decoration:none}img{max-width:100%}</style>
<style>
/* Static-preview shims: the WYSIWYG preview does NOT run the marketing sites'
   vanilla hydrators (owl carousels, preloader fade-out, scroll-reveal), so
   replicate their end-state in CSS. Selectors are theme-specific but inert on
   themes whose DOM lacks these nodes. */
.preloader{display:none!important}
.owl-carousel{display:block!important}
/* show one slide per carousel (un-hydrated slides would stack vertically) */
.owl-carousel > .slide-item ~ .slide-item{display:none!important}
/* Kathreftis banner content is opacity:0 / translated until owl marks a slide
   .active — force the revealed end-state so the hero text is visible. */
.banner-carousel .content-box .sub-title,
.banner-carousel .content-box h1,
.banner-carousel .content-box .text,
.banner-carousel .content-box .links-box,
.banner-carousel .content-box .content-image,
.banner-carousel .content-box .round-layer,
.banner-carousel .slide-item .image-layer{opacity:1!important;transform:none!important;visibility:visible!important}
</style>
</head><body><div id="mount"></div></body></html>`;

export function Preview({ blocks }: { blocks: BlockInstance[] }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const rootRef = useRef<Root | null>(null);
  const headRef = useRef<HTMLElement | null>(null);
  const [ready, setReady] = React.useState(0);

  // (Re)create the iframe document + React root once the frame is mounted.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const doc = frame.contentDocument!;
    doc.open();
    doc.write(FRAME_HTML);
    doc.close();
    const mount = doc.getElementById('mount')!;
    headRef.current = doc.head;
    // The preview is same-origin as the admin, so a block's <a href="/about">
    // would navigate the frame to the admin SPA. Keep the preview INERT: swallow
    // link clicks and form submits (this is a layout preview, not the live site).
    doc.addEventListener('click', (e) => { if ((e.target as HTMLElement)?.closest?.('a')) e.preventDefault(); }, true);
    doc.addEventListener('submit', (e) => e.preventDefault(), true);
    rootRef.current = createRoot(mount);
    setReady((n) => n + 1);
    return () => {
      rootRef.current?.unmount();
      rootRef.current = null;
    };
  }, []);

  // Render blocks whenever they change.
  useEffect(() => {
    if (!rootRef.current || !headRef.current) return;
    rootRef.current.render(
      <StyleSheetManager target={headRef.current}>
        <>{renderBlocks(blocks)}</>
      </StyleSheetManager>,
    );
  }, [blocks, ready]);

  return (
    <iframe
      ref={frameRef}
      title="Preview"
      style={{ width: '100%', height: '100%', border: 'none', background: '#fff', display: 'block' }}
    />
  );
}
